import { Box } from "@mui/material";
import { useStyles } from "./SummaryReportDashboard.styles";
import ReportCard from "components/ReportCard";
import { useTranslation } from "react-i18next";
import DowntimeSummaryByProductionLine from "./components/DowntimeSummaryByProductionLine";
import TopFiveFailureCodes from "./components/TopFiveFailureCodes";
import TopFiveSecondaryFailureCodes from "./components/TopFiveSecondaryFailureCodes";
import TotalDowntimeByMachineCenter from "./components/TotalDowntimeByMachineCenter";
import DowntimeFailureTypeByMachineCenter from "./components/DowntimeFailureTypeByMachineCenter";
import DowntimeSecondaryFailureCodeByMachineCenter from "./components/DowntimeSecondaryFailureCodeByMachineCenter";
import { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import SummaryReportDashboardFilters from "./components/SummaryReportDashboardFilters";
import { SummaryReportDashboardContext } from "contexts/SummaryReportDashboardContext/SummaryReportDashboardContext";
import DowntimeSummaryByFailureType from "./components/DowntimeSummaryByFailureType";
import { MillsEntity } from "entities/Mills/MillsEntity";
import { MillDetailEntity } from "entities/MillDetail/MillDetailEntity";
import DowntimeFailureCodeByMachineCenterReportCard from "./components/DowntimeFailureCodeByMachineCenterReportCard";
import { translateDefaultEntity } from "utilities/localesUtilities";
import { DefaultTranslationFieldsInTable } from "constants/constants";

const SummaryReportDashboard = () => {
    const classes = useStyles();
    const { t, i18n } = useTranslation();
    const {
        showFilters,
        selectedFailureType,
        selectedFailureCode,
        selectedSecondaryFailureCode,
        selectedProductionLineName,
    } = useContext(SummaryReportDashboardContext);

    const { millId } = useParams();

    const { millCountryCode } = MillsEntity.GetMillCountryCode(Number(millId));
    const { countryCode: corporateCountryCode } = MillDetailEntity.GetCorporateZipcodeAndCountry(Number(millId));
    const [countryCode, setCountryCode] = useState<string>("");

    const defaultFailureTypeTranslatedName = translateDefaultEntity(
        i18n.language,
        selectedFailureType,
        "name",
        DefaultTranslationFieldsInTable.FAILURE_TYPE,
    );

    const defaultFailureCodeTranslatedName = translateDefaultEntity(
        i18n.language,
        selectedFailureCode,
        "name",
        DefaultTranslationFieldsInTable.FAILURE_CODE,
    );

    useEffect(() => {
        if (millCountryCode) {
            setCountryCode(millCountryCode);
        } else if (corporateCountryCode && corporateCountryCode.length > 0) {
            setCountryCode(corporateCountryCode);
        }
    }, [millCountryCode, corporateCountryCode]);

    return (
        <>
            {showFilters && <SummaryReportDashboardFilters />}
            <Box className={classes.gridContainer}>
                <ReportCard title={t("locations.summary_report_dashboard.downtime_summary_by_production_line.title")}>
                    <DowntimeSummaryByProductionLine />
                </ReportCard>
                <ReportCard title={t("locations.summary_report_dashboard.downtime_summary_by_failure_type.title")}>
                    <DowntimeSummaryByFailureType countryCode={countryCode} />
                </ReportCard>
                <ReportCard
                    title={t(
                        !selectedFailureType
                            ? "locations.summary_report_dashboard.top_five_failure_codes.title_no_selected"
                            : !selectedProductionLineName
                            ? "locations.summary_report_dashboard.top_five_failure_codes.title"
                            : "locations.summary_report_dashboard.top_five_failure_codes.title_with_production_line",
                        {
                            failureType: defaultFailureTypeTranslatedName,
                            productionLine: selectedProductionLineName,
                        },
                    )}
                    noDataTitle={t("locations.summary_report_dashboard.top_five_failure_codes.no_data")}
                    showNoData={!selectedFailureType}
                >
                    <TopFiveFailureCodes countryCode={countryCode} />
                </ReportCard>
                <ReportCard
                    title={t(
                        !selectedFailureCode
                            ? "locations.summary_report_dashboard.top_five_secondary_failure_codes.title_no_selected"
                            : !selectedProductionLineName
                            ? "locations.summary_report_dashboard.top_five_secondary_failure_codes.title"
                            : "locations.summary_report_dashboard.top_five_secondary_failure_codes.title_with_production_line",
                        {
                            failureCode: defaultFailureCodeTranslatedName,
                            productionLine: selectedProductionLineName,
                        },
                    )}
                    noDataTitle={t("locations.summary_report_dashboard.top_five_secondary_failure_codes.no_data")}
                    showNoData={!selectedFailureCode}
                >
                    <TopFiveSecondaryFailureCodes countryCode={countryCode} />
                </ReportCard>
                <ReportCard title={t("locations.summary_report_dashboard.total_downtime_by_machine_center.title")}>
                    <TotalDowntimeByMachineCenter countryCode={countryCode} />
                </ReportCard>
                <ReportCard
                    title={t(
                        !selectedFailureType
                            ? "locations.summary_report_dashboard.downtime_failure_type_by_machine_center.title_no_selected"
                            : "locations.summary_report_dashboard.downtime_failure_type_by_machine_center.title",
                        {
                            failureType: defaultFailureTypeTranslatedName,
                        },
                    )}
                    noDataTitle={t(
                        "locations.summary_report_dashboard.downtime_failure_type_by_machine_center.no_data",
                    )}
                    showNoData={!selectedFailureType}
                >
                    <DowntimeFailureTypeByMachineCenter countryCode={countryCode} />
                </ReportCard>
                <DowntimeFailureCodeByMachineCenterReportCard countryCode={countryCode} />
                <ReportCard
                    title={t(
                        !selectedSecondaryFailureCode
                            ? "locations.summary_report_dashboard.downtime_secondary_failure_code_by_machine_center.title_no_selected"
                            : "locations.summary_report_dashboard.downtime_secondary_failure_code_by_machine_center.title",
                        {
                            secondaryFailureCode: defaultFailureCodeTranslatedName,
                        },
                    )}
                    noDataTitle={t(
                        "locations.summary_report_dashboard.downtime_secondary_failure_code_by_machine_center.no_data",
                    )}
                    showNoData={!selectedSecondaryFailureCode}
                >
                    <DowntimeSecondaryFailureCodeByMachineCenter countryCode={countryCode} />
                </ReportCard>
            </Box>
        </>
    );
};

export default SummaryReportDashboard;
